import { createNanoEvents, type Emitter } from 'nanoevents';
import { retry } from 'ts-retry-promise';
import { SyncClient } from 'twilio-sync';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type State = Record<string, any> & {
  _updated?: number;
  _ttl?: number;
};
type Fetcher<T> = (key: string) => Promise<T>;
type Subscriber<T> = (value: T | null) => void;
type Unsubscriber = () => void;
type Updater<T> = (value: T | null) => T;

interface Events {
  updated: (key: string) => void,
}

interface SubscriberOptions {
  onlyOnce?: boolean;
  triggerImmediately?: boolean;
  defaultValue?: State;
  stateFetcher?: Fetcher<State>;
}

const defaultFetcher = async (key: string) => {
  const response = await fetch(`/status/sync?key=${key}`);
  const data = await response.json();
  return data;
};

class StateSyncer {
  private key: string;
  private state: State | null;
  private emitter: Emitter<Events>;
  private fetcher: Fetcher<State> = defaultFetcher;
  private client: SyncClient | null = null;
  private isDirty = true;

  constructor(key: string) {
    this.key = `state_sync_${key}`;
    this.state = null;
    this.emitter = createNanoEvents<Events>();
  }

  setSyncClient(client: SyncClient) {
    if (this.client) {
      return;
    }
    const key = this.key;
    client.document({
      id: this.key,
      mode: 'open_or_create',
      ttl: 24 * 60 * 60, // 1 day
    }).then((document) => {
      document.on('updated', (event) => {
        console.log('sync document updated', event);
        this.isDirty = true;
        this.emitter.emit('updated', this.key);
      });
    }).catch(function (error) {
      console.error('Unexpected error', error, { key });
    });
    this.client = client;
  }

  async getState(refresh = false) {
    let state = this.state;
    if (refresh || this.isDirty) {
      state = await this.fetcher(this.key);
      this.state = state;
      this.isDirty = false;
    }
    return state;
  }

  public set(value: State) {
    const ttl = value._ttl ?? 60 * 60 * 8;
    fetch(`/status/sync?key=${this.key}&ttl=${ttl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(value)
    }).then((response) => response.json()).then((data) => {
      this.state = data;
      this.isDirty = false;
    });
  }

  public update(updater: Updater<State>, refresh = false) {
    const state = this.getState(refresh);
    const newState = updater(state);
    this.set(newState);
  }

  public remove() {
    this.set({ _ttl: 0 });
  }

  public subscribe(run: Subscriber<State>, options?: SubscriberOptions): Unsubscriber {
    if (options?.stateFetcher) {
      this.fetcher = options.stateFetcher;
    }
    const onUpdate = async () => {
      let data = await this.getState(true);
      if (!data && options?.defaultValue) {
        data = options.defaultValue;
      }
      run(data);
    };

    if (options?.triggerImmediately) {
      onUpdate();
    }

    const off = this.emitter.on('updated', onUpdate);

    if (options?.onlyOnce) {
      off();
    }
    return off;
  }

  public unsubscribeAll() {
    this.emitter.events = {};
  }
}

const tokenFetcher = async () => {
  let gid = 0;
  try {
    gid = await retry(() => {
      if (!window._current_expoid) {
        return Promise.reject(new Error('no _current_expoid'));
      }
      return Promise.resolve(window._current_expoid());
    }, { delay: 300, retries: Infinity, timeout: 5000 });
  } catch (error) {
    throw new Error("get expoid error", { cause: error });
  }
  const query = new URLSearchParams(`type=sync&reset=1&gid=${gid}`);
  const url = `/twilio/token/${gid}?${query.toString()}`;
  const rsp = await fetch(url);
  const data = await rsp.json();
  return data["token"] as string;
};

async function getTwilioSyncClient() {
  let client = window.Toolbox?.twilio?.sync_client;
  if (!client) {
    let token = window.Drupal?.settings?.twilio_sync_token;
    if (!token) {
      try {
        token = await tokenFetcher();
      } catch (error) {
        throw new Error("get twilio sync token error", { cause: error });
      }
    }
    client = new SyncClient(token);
    client.on('tokenAboutToExpire', () => {
      tokenFetcher().then(token => {
        console.log('update sync token');
        client.updateToken(token);
      });
    });
    client.on('connectionError', (connectionError) => {
      console.error('Connection was interrupted:', connectionError.message, 'Is terminal:', connectionError.terminal);
    });
  }
  return client;
}

class StateSyncStore {
  private syncers: Map<string, StateSyncer>;
  private client: SyncClient | null = null;

  constructor() {
    this.syncers = new Map();
    getTwilioSyncClient().then(client => {
      console.log('getTwilioSyncClient', client);
      this.client = client;
      this.syncers.forEach((syncer) => {
        syncer.setSyncClient(client);
      });
    }).catch(e => {
      throw new Error("get twilio sync client error", { cause: e });
    });
  }

  private getSyncer(key: string): StateSyncer {
    if (!this.syncers.has(key)) {
      const syncer = new StateSyncer(key);
      if (this.client) {
        syncer.setSyncClient(this.client);
      }
      this.syncers.set(key, syncer);
    }
    return this.syncers.get(key)!;
  }

  public set(key: string, value: State) {
    this.getSyncer(key).set(value);
  }
  public getState(key: string, refresh = false) {
    return this.getSyncer(key).getState(refresh);
  }

  public update(key: string, updater: Updater<State>, refresh = false) {
    this.getSyncer(key).update(updater, refresh);
  }

  public remove(key: string) {
    this.getSyncer(key).remove();
  }

  public subscribe(key: string, run: Subscriber<State>, options?: SubscriberOptions): Unsubscriber {
    return this.getSyncer(key).subscribe(run, options);
  }

  public unsubscribeAll(key: string) {
    this.getSyncer(key).unsubscribeAll();
  }
}

const SyncStateStore = new StateSyncStore();
// Object.freeze(SyncStateStore);

export { SyncStateStore, getTwilioSyncClient, type StateSyncStore };
